import { CategoryChartQueryFn, CategoryChartQueryParam } from "../categoryChart";
import { deltaValue, roundValue, Unit, ValueStatus } from "../processors";
import { FigureSolidDashKpiProviders } from "./categoryChartData.types";
import { figureDeltaString } from "./figureDeltaString";
import _ from "lodash";
import { getKpisForProvidersWithCategoryChart } from "../opendata";
import {
    // getAverageRealERateReturn,
    getMedianRealERateReturn,
    getRealERateReturn
} from "./fetchFigureSolidDashRealERateReturnKpiProviderChartData";
import { extractInitialYearWith2DimValues, PROVIDER_TITLE_CLASS, VALUE_STATUS_NAMING } from "../misc";
import { getConnectionSizeMedianText } from "../../utils";

const providerTitleStartEndDiv = {
    providerTitleStartSection: `<div class="${PROVIDER_TITLE_CLASS}">`,
    providerTitleEndSection: `</div>`
};

export const fetchFigureSolidDashRealERateReturnKpiComparisonChartData: CategoryChartQueryFn = async ({
    metadata,
    categoryChart,
    providers,
    kpis,
    category
}: CategoryChartQueryParam): Promise<FigureSolidDashKpiProviders> => {
    if (!metadata || !categoryChart || !providers || !providers.filter(d => d != null).length) return null;

    const kpiReportResults = await getKpisForProvidersWithCategoryChart(kpis, providers, true, categoryChart);

    if (!kpiReportResults) return null;

    const groupByYearKpiReportResults = _.groupBy(kpiReportResults, d => d.year);
    const unit: Unit = "%";

    let years = _.orderBy(_.uniq(kpiReportResults.map(d => d.year)), d => d, "asc");
    const providerValues = _.flattenDeep(
        providers.map(provider =>
            years
                .map(year => {
                    const reports = groupByYearKpiReportResults[year];
                    const providerValue = getRealERateReturn(provider, kpis, reports);
                    const providerValueStatus =
                        providerValue != null
                            ? null
                            : reports?.find(d => d?.provider?.tid === provider?.tid)?.valueStatus;

                    return {
                        year: year,
                        providerTid: provider?.tid,
                        providerValue,
                        providerValueStatus
                    };
                })
                .filter(d => (d?.providerValue != null || d?.providerValueStatus != null) && d?.providerTid != null)
        )
    );

    const providerTidToProviderValue = _.groupBy(providerValues, d => d.providerTid);

    years = _.sortBy(_.uniq(providerValues.map(d => d.year).filter(d => d != null)), d => d, "asc");
    const latestYear = years?.[years.length - 1];
    const previousYear = years?.[years.length - 2];

    // kpi report results for the provider
    const providerTidToYearData = providers
        ?.map(provider => {
            const yearAsc = _.orderBy(providerTidToProviderValue?.[provider?.tid] ?? [], d => d.year, "asc");
            const yearToResults = _.keyBy(yearAsc, d => d.year);
            return {
                providerId: provider?.tid,
                yearAsc,
                yearToResults
            };
        })
        .reduce<
            Record<
                string,
                {
                    yearAsc: {
                        year: number;
                        providerTid: number;
                        providerValue: number;
                    }[];
                    yearToResults: Record<
                        number,
                        {
                            year: number;
                            providerTid: number;
                            providerValue: number;
                            providerValueStatus: ValueStatus;
                        }
                    >;
                    indicatorTitle: string;
                }
            >
        >((prev, { providerId, yearAsc, yearToResults }) => {
            prev[providerId] = {
                yearAsc,
                yearToResults: yearToResults as Record<
                    number,
                    {
                        year: number;
                        providerTid: number;
                        providerValue: number;
                        providerValueStatus: ValueStatus;
                    }
                >,
                indicatorTitle: "Economic real rate of return"
            };

            return prev;
        }, {});

    const { displayTitle, visHeader, visFooter, figureHeader, figureFooter, visType } = categoryChart;

    // lines data
    const linesData =
        providers
            ?.map(provider => {
                const { yearToResults } = providerTidToYearData?.[provider?.tid] ?? {};

                return (
                    years?.map(year => {
                        if (!yearToResults[year]) return undefined;

                        const result = yearToResults[year];

                        return roundValue(result?.providerValue ?? 0, unit, true, false).value;
                    }) ?? []
                );
            })
            ?.filter(d => d != null) ?? [];

    const measure = kpis?.[0]?.[2] ?? "Economic real rate of return";

    const linesDataSeriesName =
        providers
            ?.map(provider => {
                // if (!providerTidToYearData?.[provider?.tid]) return null;
                // const { indicatorTitle } = providerTidToYearData?.[provider?.tid];

                return `${provider.name}`;
            })
            ?.filter(d => d != null) ?? [];

    // average provider line data
    // const largeProviderAverageSeriesName = "Large provider median";
    const connectionSizeCategories = _.uniq(providers?.map(d => d.connectionSizeCategory)?.filter(d => d != null));
    const providerMedianSeriesNames = connectionSizeCategories.map(connectionSizeCategory =>
        getConnectionSizeMedianText(metadata, connectionSizeCategory)
    );

    const averages = connectionSizeCategories.map(connectionSizeCategory => {
        return (
            years?.map(year => {
                const yearBasedReports = groupByYearKpiReportResults?.[year] ?? [];
                const conSizeCategoryProviders = yearBasedReports.filter(
                    d => d?.provider?.connectionSizeCategory === connectionSizeCategory
                );

                const average = getMedianRealERateReturn(kpis, conSizeCategoryProviders);

                return roundValue(average ?? 0, unit as Unit, true, false).value;
            }) ?? []
        );
    });

    const initialYear = extractInitialYearWith2DimValues(years, linesData, averages);
    // graph header and footer
    const visHeaderProcessed = _.template(visHeader?.value ?? "")({
        startYear: initialYear,
        endYear: latestYear
    });

    const visFooterProcessed = _.template(visFooter?.value ?? "")({
        startYear: initialYear,
        endYear: latestYear
    });

    // graph instance
    const graph = {
        measure,
        header:
            !visHeader || visHeader.processed.trim() === ""
                ? null
                : {
                      processed: visHeaderProcessed,
                      value: visHeaderProcessed,
                      format: visHeaderProcessed
                  },
        footer:
            !visFooter || visFooter.processed.trim() === ""
                ? null
                : {
                      processed: visFooterProcessed,
                      value: visFooterProcessed,
                      format: visFooterProcessed
                  },

        years,

        providerMedianSeriesNames,
        providerMedianAnnotations: providerMedianSeriesNames,
        providerMedianData: averages,

        linesData,
        linesDataSeriesName
    };
    // figures
    const figures =
        providers
            ?.map((provider, providerIndex) => {
                const providerExist = providerTidToYearData?.[provider?.tid] != null;
                const { yearToResults } = providerTidToYearData?.[provider?.tid] ?? {};

                const latestYearResult = yearToResults?.[latestYear];
                const previousYearResult = yearToResults?.[previousYear];

                const { providerValue: previousYearValue } = previousYearResult ?? {};
                const { providerValue: latestYearValue, providerValueStatus: latestYearValueStatus } =
                    latestYearResult ?? {};

                // if data does not exist, return NA/ND view
                if (!providerExist || latestYearValue == null) {
                    const figureHeaderProcessed = _.template(figureHeader?.value ?? "")({
                        ...providerTitleStartEndDiv,
                        year: null,
                        value: null,
                        deltaString: null,
                        providerName: provider.name
                    });

                    const figureFooterProcessed = _.template(figureFooter?.value ?? "")({
                        ...providerTitleStartEndDiv,
                        year: null,
                        value: null,
                        deltaString: null,
                        providerName: provider.name
                    });

                    return {
                        header:
                            !figureHeader || figureHeader.processed.trim() === ""
                                ? null
                                : {
                                      processed: figureHeaderProcessed,
                                      value: figureHeaderProcessed,
                                      format: figureHeaderProcessed
                                  },
                        footer:
                            !figureFooter || figureFooter.processed.trim() === ""
                                ? null
                                : {
                                      processed: figureFooterProcessed,
                                      value: figureFooterProcessed,
                                      format: figureFooterProcessed
                                  },
                        value:
                            latestYearValueStatus != null
                                ? VALUE_STATUS_NAMING?.[latestYearValueStatus] ?? latestYearValueStatus
                                : !providerExist
                                ? VALUE_STATUS_NAMING.NR
                                : previousYearValue != null && latestYearValue == null
                                ? VALUE_STATUS_NAMING.MD
                                : VALUE_STATUS_NAMING.NR,
                        decimals: 0
                    };
                }

                const delta = deltaValue(previousYearValue, latestYearValue, unit);

                const deltaString = figureDeltaString(delta, category, providerIndex);
                const figureHeaderProcessed = _.template(figureHeader?.value ?? "")({
                    ...providerTitleStartEndDiv,
                    year: `${latestYear}`,
                    value: latestYearValue,
                    deltaString,
                    providerName: provider.name
                });

                const figureFooterProcessed = _.template(figureFooter?.value ?? "")({
                    ...providerTitleStartEndDiv,
                    year: `${latestYear}`,
                    value: latestYearValue,
                    deltaString,
                    providerName: provider.name
                });

                return {
                    header:
                        !figureHeader || figureHeader.processed.trim() === ""
                            ? null
                            : {
                                  processed: figureHeaderProcessed,
                                  value: figureHeaderProcessed,
                                  format: figureHeaderProcessed
                              },
                    footer:
                        !figureFooter || figureFooter.processed.trim() === ""
                            ? null
                            : {
                                  processed: figureFooterProcessed,
                                  value: figureFooterProcessed,
                                  format: figureFooterProcessed
                              },
                    value: roundValue(latestYearValue, unit, true, false).value,
                    decimals: roundValue(latestYearValue, unit, true, false).decimals
                };
            })
            ?.filter(d => d != null) ?? [];

    // kpi report results for the provider
    const ret: FigureSolidDashKpiProviders = {
        title: displayTitle,
        kpiCode: kpis?.[0]?.[0],
        schemeType: kpis?.[0]?.[1],
        waterProviders: providers,
        unit,
        year: years?.[years?.length - 1],
        figures: !(visType === "figure" || visType === "figureWithSolidDashLine") ? null : figures,
        graph: visType === "figure" ? null : graph,
        footer: null
    };

    return ret;
};
